import React, { useEffect, useMemo, useState } from "react";
import {
  FaTimes,
  FaSave,
  FaHandshake,
  FaUser,
  FaBuilding,
  FaEnvelope,
  FaPhone,
  FaUsers,
} from "react-icons/fa";

const emptyForm = {
  nom_parrain: "",
  prenom_parrain: "",
  club_parrain: "",
  email_parrain: "",
  telephone_parrain: "",
  club_parraine: "",
  nom_contact_parraine: "",
  prenom_contact_parraine: "",
  email_parraine: "",
  telephone_parraine: "",
};

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:ring-indigo-900/40";

const AddParrainageModal = ({ isOpen, onClose, onAdd, initialData = null }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    if (initialData) {
      setFormData({
        ...emptyForm,
        ...initialData,
      });
    } else {
      setFormData(emptyForm);
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, initialData, onClose]);

  const title = initialData ? "Modifier un parrainage" : "Ajouter un parrainage";
  const subtitle = initialData
    ? "Mets à jour les informations du parrain, du club et du contact parrainé."
    : "Crée un nouveau lien de parrainage entre un club référent et un club recommandé.";

  const sponsorFullName = useMemo(() => {
    return `${formData.nom_parrain || ""} ${formData.prenom_parrain || ""}`.trim();
  }, [formData.nom_parrain, formData.prenom_parrain]);

  const filleulFullName = useMemo(() => {
    return `${formData.nom_contact_parraine || ""} ${formData.prenom_contact_parraine || ""}`.trim();
  }, [formData.nom_contact_parraine, formData.prenom_contact_parraine]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    if (initialData) {
      setFormData({
        ...emptyForm,
        ...initialData,
      });
    } else {
      setFormData(emptyForm);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSaving(true);
    const ok = await onAdd(formData);
    setSaving(false);

    if (ok) {
      onClose?.();
      setFormData(emptyForm);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1200]">
      {/* overlay */}
      <div
        className="absolute inset-0 bg-slate-950/70 backdrop-blur-[3px]"
        onClick={onClose}
      />

      {/* modal */}
      <div className="absolute inset-0 flex items-center justify-center p-3 sm:p-6">
        <div className="relative flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-[28px] border border-white/10 bg-white shadow-[0_30px_90px_-30px_rgba(0,0,0,0.45)] dark:bg-slate-900">
          {/* header */}
          <div className="relative overflow-hidden border-b border-slate-200 dark:border-slate-800">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(99,102,241,0.26),transparent_28%),radial-gradient(circle_at_top_right,rgba(236,72,153,0.22),transparent_24%),linear-gradient(135deg,#0f172a_0%,#1e293b_45%,#312e81_100%)]" />
            <div className="relative px-5 py-5 sm:px-6 sm:py-6 text-white">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-white/80">
                    <FaHandshake />
                    Parrainage
                  </div>

                  <h3 className="mt-3 text-2xl font-semibold tracking-tight">
                    {title}
                  </h3>

                  <p className="mt-1 max-w-2xl text-sm text-white/75">
                    {subtitle}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/15 bg-white/10 text-white transition hover:bg-white/20"
                  aria-label="Fermer"
                >
                  <FaTimes />
                </button>
              </div>
            </div>
          </div>

          {/* body */}
          <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-950/60">
            <form onSubmit={handleSubmit} className="p-4 sm:p-6">
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
                {/* colonne principale */}
                <div className="xl:col-span-8 space-y-6">
                  <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <SectionTitle
                      title="Parrain"
                      subtitle="Informations sur le club ou le contact qui recommande."
                    />

                    <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                      <Field>
                        <Label required>Nom du parrain</Label>
                        <div className="relative">
                          <FaUser className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input
                            type="text"
                            name="nom_parrain"
                            value={formData.nom_parrain}
                            onChange={handleChange}
                            className={`${inputClass} pl-10`}
                            placeholder="Nom"
                            required
                          />
                        </div>
                      </Field>

                      <Field>
                        <Label required>Prénom du parrain</Label>
                        <div className="relative">
                          <FaUser className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input
                            type="text"
                            name="prenom_parrain"
                            value={formData.prenom_parrain}
                            onChange={handleChange}
                            className={`${inputClass} pl-10`}
                            placeholder="Prénom"
                            required
                          />
                        </div>
                      </Field>

                      <Field>
                        <Label required>Club parrain</Label>
                        <div className="relative">
                          <FaBuilding className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input
                            type="text"
                            name="club_parrain"
                            value={formData.club_parrain}
                            onChange={handleChange}
                            className={`${inputClass} pl-10`}
                            placeholder="Nom du club parrain"
                            required
                          />
                        </div>
                      </Field>

                      <Field>
                        <Label required>Email du parrain</Label>
                        <div className="relative">
                          <FaEnvelope className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input
                            type="email"
                            name="email_parrain"
                            value={formData.email_parrain}
                            onChange={handleChange}
                            className={`${inputClass} pl-10`}
                            placeholder="email@club.fr"
                            required
                          />
                        </div>
                      </Field>

                      <Field className="md:col-span-2">
                        <Label required>Téléphone du parrain</Label>
                        <div className="relative">
                          <FaPhone className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input
                            type="text"
                            name="telephone_parrain"
                            value={formData.telephone_parrain}
                            onChange={handleChange}
                            className={`${inputClass} pl-10`}
                            placeholder="06 00 00 00 00"
                            required
                          />
                        </div>
                      </Field>
                    </div>
                  </section>

                  <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <SectionTitle
                      title="Club parrainé"
                      subtitle="Informations sur le club recommandé et son contact principal."
                    />

                    <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                      <Field className="md:col-span-2">
                        <Label required>Club parrainé</Label>
                        <div className="relative">
                          <FaBuilding className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input
                            type="text"
                            name="club_parraine"
                            value={formData.club_parraine}
                            onChange={handleChange}
                            className={`${inputClass} pl-10`}
                            placeholder="Nom du club parrainé"
                            required
                          />
                        </div>
                      </Field>

                      <Field>
                        <Label required>Nom du contact</Label>
                        <div className="relative">
                          <FaUsers className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input
                            type="text"
                            name="nom_contact_parraine"
                            value={formData.nom_contact_parraine}
                            onChange={handleChange}
                            className={`${inputClass} pl-10`}
                            placeholder="Nom"
                            required
                          />
                        </div>
                      </Field>

                      <Field>
                        <Label required>Prénom du contact</Label>
                        <div className="relative">
                          <FaUsers className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input
                            type="text"
                            name="prenom_contact_parraine"
                            value={formData.prenom_contact_parraine}
                            onChange={handleChange}
                            className={`${inputClass} pl-10`}
                            placeholder="Prénom"
                            required
                          />
                        </div>
                      </Field>

                      <Field>
                        <Label required>Email du contact</Label>
                        <div className="relative">
                          <FaEnvelope className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input
                            type="email"
                            name="email_parraine"
                            value={formData.email_parraine}
                            onChange={handleChange}
                            className={`${inputClass} pl-10`}
                            placeholder="email@club.fr"
                            required
                          />
                        </div>
                      </Field>

                      <Field>
                        <Label required>Téléphone du contact</Label>
                        <div className="relative">
                          <FaPhone className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input
                            type="text"
                            name="telephone_parraine"
                            value={formData.telephone_parraine}
                            onChange={handleChange}
                            className={`${inputClass} pl-10`}
                            placeholder="06 00 00 00 00"
                            required
                          />
                        </div>
                      </Field>
                    </div>
                  </section>
                </div>

                {/* colonne résumé */}
                <div className="xl:col-span-4 space-y-6">
                  <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <SectionTitle
                      title="Résumé"
                      subtitle="Vérifie les informations avant validation."
                    />

                    <div className="mt-5 space-y-4">
                      <SummaryRow
                        icon={<FaHandshake />}
                        label="Type"
                        value={initialData ? "Modification" : "Nouveau parrainage"}
                      />
                      <SummaryRow
                        icon={<FaUser />}
                        label="Parrain"
                        value={sponsorFullName || "Non renseigné"}
                      />
                      <SummaryRow
                        icon={<FaBuilding />}
                        label="Club parrain"
                        value={formData.club_parrain || "Non renseigné"}
                      />
                      <SummaryRow
                        icon={<FaBuilding />}
                        label="Club parrainé"
                        value={formData.club_parraine || "Non renseigné"}
                      />
                      <SummaryRow
                        icon={<FaUsers />}
                        label="Contact parrainé"
                        value={filleulFullName || "Non renseigné"}
                      />
                    </div>
                  </section>

                  <section className="rounded-[24px] border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-fuchsia-50 p-5 shadow-sm dark:border-indigo-900/50 dark:from-indigo-950/40 dark:via-slate-900 dark:to-fuchsia-950/20">
                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-600 dark:text-indigo-400">
                      Conseil
                    </div>
                    <h4 className="mt-2 text-lg font-semibold text-slate-900 dark:text-slate-50">
                      Soigne les coordonnées
                    </h4>
                    <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                      Des e-mails et téléphones propres te permettront d’exploiter
                      plus facilement ton réseau de recommandation ensuite.
                    </p>
                  </section>
                </div>
              </div>
            </form>
          </div>

          {/* footer */}
          <div className="border-t border-slate-200 bg-white/95 px-4 py-4 backdrop-blur sm:px-6 dark:border-slate-800 dark:bg-slate-900/95">
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="button"
                onClick={resetForm}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                Réinitialiser
              </button>

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                >
                  <FaTimes />
                  Annuler
                </button>

                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={saving}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 via-violet-600 to-pink-600 px-4 py-2.5 text-sm font-medium text-white transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaSave />
                  {saving
                    ? "Enregistrement..."
                    : initialData
                    ? "Enregistrer les modifications"
                    : "Ajouter le parrainage"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function SectionTitle({ title, subtitle }) {
  return (
    <div>
      <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
        {title}
      </h4>
      {subtitle ? (
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

function Field({ children }) {
  return <div>{children}</div>;
}

function Label({ children, required = false }) {
  return (
    <label className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">
      {children}
      {required ? <span className="ml-1 text-rose-500">*</span> : null}
    </label>
  );
}

function SummaryRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-800/50">
      <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-slate-700 shadow-sm dark:bg-slate-900 dark:text-slate-200">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
          {label}
        </div>
        <div className="mt-1 break-words text-sm font-medium text-slate-900 dark:text-slate-50">
          {value || "—"}
        </div>
      </div>
    </div>
  );
}

export default AddParrainageModal;